import React, { useState } from 'react';
import {
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useContext } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { OrganisationSettings } from '../../../../interfaces/delivery-company/orders';

interface LocationTrackingConfigProps {
  settings: OrganisationSettings;
}

export default function LocationTrackingConfig({ settings }: LocationTrackingConfigProps) {
  const { config } = useContext(AuthContext);
  const [enabled, setEnabled] = useState(settings.driver_location_tracking_enabled ?? false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await axios.post(
        `${API_ENDPOINT}/api/v1/settings`,
        { driver_location_tracking_enabled: newValue },
        config
      );
      setEnabled(newValue);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update location tracking settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={handleToggle}
            disabled={loading}
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body1" fontWeight="medium">
              Enable Driver Location Tracking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track drivers locations when they are on duty
            </Typography>
          </Box>
        }
      />

      {loading && (
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Updating...</Typography>
        </Box>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Location tracking settings updated successfully
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        When enabled, drivers will be required to share their location while on duty.
        This helps with route optimization and delivery management. Individual drivers
        can be configured to override this global setting.
      </Alert>
    </Box>
  );
}




